/**
 * Email Agent — magic-link request/verify and (future) generic email send.
 *
 * Mirrors the static-class pattern used by `negotiationAgent.js` and the
 * function-export pattern used by `docusignAgent.js`: this file uses the
 * static-class style and re-exports the standalone helpers + default agent
 * object so call sites can pick whichever import shape they prefer.
 *
 * Backend contracts: docs/email-service/api-contract.md §3.
 *   - POST /api/auth/magic-link/request  →  always 202 (enumeration-safe)
 *   - GET  /api/auth/magic-link/verify   →  { user, session_token } on success,
 *                                            400 { error: "invalid_or_expired" } on failure.
 *
 * Notes
 * -----
 * `requestMagicLink` deliberately swallows 4xx responses (other than 5xx /
 * network) because the backend's enumeration-safe contract guarantees a 202
 * for any well-formed request. Network errors and 5xx still propagate so the
 * UI can render an inline retry.
 *
 * `verifyMagicLink` propagates 4xx so the landing page can branch on
 * "invalid_or_expired" vs network failure.
 */

import APIAgent, { API_BASE_URL } from './apiAgent';

/** Allowed magic-link purposes (must match backend `MagicLinkPurpose` enum). */
export const MAGIC_LINK_PURPOSES = Object.freeze({
  LOGIN: 'login',
  EMAIL_VERIFY: 'email_verify',
  PASSWORD_RESET: 'password_reset',
});

/**
 * Typed error raised when the backend returns 400 from verify, signalling the
 * token was bad, already consumed, or expired. The landing page distinguishes
 * this from network errors so it can offer "request a new link" only when
 * it makes sense.
 */
export class MagicLinkInvalidError extends Error {
  constructor(message = 'Link is invalid or has expired.', detail = null) {
    super(message);
    this.name = 'MagicLinkInvalidError';
    this.detail = detail;
  }
}

class EmailAPI {
  /**
   * Request a magic-link email. Always treats well-formed responses as success
   * (the backend returns 202 even when the email is unknown to prevent user
   * enumeration). Only network failures or 5xx propagate as errors.
   *
   * @param {{ email: string, purpose?: string, redirectTo?: string }} params
   * @returns {Promise<{ message: string }>} Always resolves on 2xx; resolves
   *   with a generic acknowledgement on 4xx so the UI can stay neutral.
   */
  static async requestMagicLink({ email, purpose = MAGIC_LINK_PURPOSES.LOGIN, redirectTo }) {
    const payload = { email, purpose };
    if (redirectTo) payload.redirect_path = redirectTo;

    try {
      return await APIAgent.request(
        '/api/auth/magic-link/request',
        'POST',
        payload,
        {},
        { skipAuthRedirect: true, timeout: 10000 },
      );
    } catch (err) {
      const status = err?.status ?? err?.statusCode ?? 0;
      // Per the enumeration-safe contract the request endpoint should never
      // return a meaningful 4xx — coerce those to a neutral ack so the UI
      // never reveals which addresses exist. Only surface network/5xx.
      if (status >= 400 && status < 500) {
        return { message: 'If an account exists for that address, a link was sent.' };
      }
      console.error('emailAgent.requestMagicLink failed:', err);
      throw err;
    }
  }

  /**
   * Verify a magic-link token. The backend honours the token itself as the
   * credential, so this is unauthenticated.
   *
   * @param {{ token: string, purpose?: string }} params
   * @returns {Promise<object>} Backend payload (e.g. `{ user, session_token }`
   *   for `login`; shape varies by purpose — pass through verbatim).
   * @throws {MagicLinkInvalidError} 400 from backend (bad / expired / used).
   * @throws {Error} For network / 5xx errors.
   */
  static async verifyMagicLink({ token, purpose = MAGIC_LINK_PURPOSES.LOGIN }) {
    if (!token) {
      throw new MagicLinkInvalidError('Missing token.');
    }
    const params = new URLSearchParams({ token, purpose });
    try {
      return await APIAgent.request(
        `/api/auth/magic-link/verify?${params.toString()}`,
        'GET',
        null,
        {},
        { skipAuthRedirect: true, timeout: 10000 },
      );
    } catch (err) {
      const status = err?.status ?? err?.statusCode ?? 0;
      if (status === 400 || status === 404 || status === 410) {
        throw new MagicLinkInvalidError(
          'This sign-in link is invalid or has expired.',
          err?.message ?? null,
        );
      }
      console.error('emailAgent.verifyMagicLink failed:', err);
      throw err;
    }
  }
}

// Convenience function-export shape (mirrors docusignAgent.js style).
export const requestMagicLink = (params) => EmailAPI.requestMagicLink(params);
export const verifyMagicLink = (params) => EmailAPI.verifyMagicLink(params);

// Re-export for completeness so callers can build absolute deep-links if
// needed (e.g. for "open in new tab" affordances).
export { API_BASE_URL };

export default EmailAPI;
